import { UserPermission, UserType, ProductLineAccess } from '../types/admin/accessManagement.types';

/**
 * Permission utility functions for centralized permission logic
 * These functions provide the core permission checking logic used throughout the application
 */

// ============================================================================
// User Type Checks
// ============================================================================

/**
 * Check if user is a Tech Admin
 * Tech Admins have the highest level of access
 */
export const isTechAdmin = (userPermission: UserPermission | null): boolean => {
  if (!userPermission) return false;
  return userPermission.user_type === UserType.TECH_ADMIN;
};

/**
 * Check if user is a Verity Admin
 * Verity Admins have full business access to all product lines including confidential data
 */
export const isVerityAdmin = (userPermission: UserPermission | null): boolean => {
  if (!userPermission) return false;
  return userPermission.user_type === UserType.VERITY_ADMIN;
};

/**
 * Check if user is Read Only
 * Read Only users can only view data, no edit access
 */
export const isReadOnly = (userPermission: UserPermission | null): boolean => {
  if (!userPermission) return false;
  return userPermission.user_type === UserType.READ_ONLY;
};

/**
 * Check if user has Extended Access for a specific product line
 * Extended Access users have elevated privileges for specific product lines
 */
export const hasExtendedAccessPL = (userPermission: UserPermission | null): boolean => {
  if (!userPermission) return false;
  return userPermission.user_type === UserType.EXTENDED_ACCESS_PL;
};

// ============================================================================
// Product Line Access Checks
// ============================================================================

/**
 * Check if user has access to view a product line
 *
 * Access Model: ALL authenticated users can view ALL product lines by default.
 * The only restriction is confidential RPNs within product lines.
 *
 * The product_lines_access array defines SPECIAL permissions (PL Admin, Confidential Access),
 * NOT basic visibility. Users without entries in product_lines_access can still VIEW all PLs.
 *
 * @param userPermission - User permission object
 * @param _productLineId - Product line ID (unused, kept for API consistency)
 * @returns True for all authenticated users
 */
export const hasProductLineAccess = (userPermission: UserPermission | null, _productLineId: number): boolean => {
  // All authenticated users can view all product lines
  return userPermission !== null;
};

/**
 * Check if user is a Product Line Admin for a specific product line
 */
export const isProductLineAdmin = (userPermission: UserPermission | null, productLineId: number): boolean => {
  if (!userPermission) return false;

  // Tech Admins and Verity Admins are admins of all product lines
  if (isTechAdmin(userPermission) || isVerityAdmin(userPermission)) {
    return true;
  }

  // Check if user has PL Admin access for this product line
  const plAccess = userPermission.product_lines_access.find((pl) => pl.product_line_id === productLineId);
  return plAccess?.is_pl_admin || false;
};

/**
 * Check if user has confidential access for a specific product line
 */
export const hasConfidentialAccessForProductLine = (
  userPermission: UserPermission | null,
  productLineId: number
): boolean => {
  if (!userPermission) return false;

  // Tech Admins and Verity Admins have confidential access to all product lines
  if (isTechAdmin(userPermission) || isVerityAdmin(userPermission)) {
    return true;
  }

  // Check if user has confidential access for this product line
  const plAccess = userPermission.product_lines_access.find((pl) => pl.product_line_id === productLineId);
  return plAccess?.has_confidential_access || false;
};

/**
 * Get product line access details for a user
 */
export const getProductLineAccess = (
  userPermission: UserPermission | null,
  productLineId: number
): ProductLineAccess | null => {
  if (!userPermission) return null;
  return userPermission.product_lines_access.find((pl) => pl.product_line_id === productLineId) || null;
};

// ============================================================================
// RPN/Program Access Checks
// ============================================================================

/**
 * Check if user can access a confidential RPN
 * Requires:
 * 1. Tech Admin, OR
 * 2. Has confidential access for the RPN's product line
 */
export const canAccessConfidentialRPN = (
  userPermission: UserPermission | null,
  productLineId: number,
  isConfidential: boolean
): boolean => {
  // Non-confidential RPNs are accessible to anyone with product line access
  if (!isConfidential) {
    return hasProductLineAccess(userPermission, productLineId);
  }

  // Confidential RPNs require special access
  return hasConfidentialAccessForProductLine(userPermission, productLineId);
};

// ============================================================================
// Edit Permission Checks
// ============================================================================

/**
 * Check if user can edit data for a specific product line
 *
 * Rules:
 * - Read Only: Cannot edit
 * - Tech Admin: Can edit all product lines
 * - Verity Admin: Can edit all product lines
 * - Extended Access (PL): Can edit ONLY product lines where they are PL Admin
 */
export const canEditProductLine = (userPermission: UserPermission | null, productLineId: number): boolean => {
  if (!userPermission || isReadOnly(userPermission)) {
    return false;
  }

  // Tech Admin and Verity Admin can edit all product lines
  if (isTechAdmin(userPermission) || isVerityAdmin(userPermission)) {
    return true;
  }

  // Extended Access (PL): Can only edit product lines where they are PL Admin
  const plAccess = userPermission.product_lines_access.find((pl) => pl.product_line_id === productLineId);
  return plAccess?.is_pl_admin || false;
};

/**
 * Check if user can edit a specific RPN.
 * Both confidential and non-confidential RPNs require PL Admin.
 */
export const canEditRPN = (userPermission: UserPermission | null, productLineId: number): boolean => {
  if (!userPermission || isReadOnly(userPermission)) return false;
  if (isTechAdmin(userPermission) || isVerityAdmin(userPermission)) return true;
  return isProductLineAdmin(userPermission, productLineId);
};

// ============================================================================
// Admin Permission Checks
// ============================================================================

/**
 * Check if user can manage access management (add/edit users)
 * Only Tech Admins can manage user permissions
 */
export const canManageAccessManagement = (userPermission: UserPermission | null): boolean => {
  return isTechAdmin(userPermission);
};

/**
 * Check if user can access the admin section
 * Tech Admins and Verity Admins can access admin section
 */
export const canAccessAdminSection = (userPermission: UserPermission | null): boolean => {
  return isTechAdmin(userPermission) || isVerityAdmin(userPermission);
};

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get all product line IDs the user has access to
 */
export const getAccessibleProductLineIds = (userPermission: UserPermission | null): number[] => {
  if (!userPermission) return [];

  // Tech Admins and Verity Admins theoretically have access to all,
  // but we return explicit list for filtering purposes
  return userPermission.product_lines_access.map((pl) => pl.product_line_id);
};

/**
 * Get all product line IDs where user is a PL Admin
 */
export const getAdminProductLineIds = (userPermission: UserPermission | null): number[] => {
  if (!userPermission) return [];

  if (isTechAdmin(userPermission) || isVerityAdmin(userPermission)) {
    // Tech Admins and Verity Admins are admins of all product lines
    return getAccessibleProductLineIds(userPermission);
  }

  return userPermission.product_lines_access.filter((pl) => pl.is_pl_admin).map((pl) => pl.product_line_id);
};

/**
 * Get all product line IDs where user has confidential access
 */
export const getConfidentialAccessProductLineIds = (userPermission: UserPermission | null): number[] => {
  if (!userPermission) return [];

  if (isTechAdmin(userPermission) || isVerityAdmin(userPermission)) {
    // Tech Admins and Verity Admins have confidential access to all product lines
    return getAccessibleProductLineIds(userPermission);
  }

  return userPermission.product_lines_access.filter((pl) => pl.has_confidential_access).map((pl) => pl.product_line_id);
};

/**
 * Check if user has any meaningful permissions (not just Read Only with no product lines)
 */
export const hasAnyPermissions = (userPermission: UserPermission | null): boolean => {
  if (!userPermission) return false;

  // Tech Admin, Verity Admin, or Extended Access always have permissions
  if (isTechAdmin(userPermission) || isVerityAdmin(userPermission) || hasExtendedAccessPL(userPermission)) {
    return true;
  }

  // Read Only users with at least one product line have permissions
  if (isReadOnly(userPermission) && userPermission.product_lines_access.length > 0) {
    return true;
  }

  return false;
};
